import type ImageType from "@travily/types/shared/Image";
import type { AvatarColor } from "@ui/components/Avatar";
import type { StickerIconName } from "@ui/components/Icon/icons";
import type { DateValue } from "@ui/primitives/shared";

export interface WhereSuggestion {
  id: string;
  name: string;
  icon: React.ReactNode;
}

export type WhereSelection =
  | { kind: "suggestion"; id: string; name: string }
  | { kind: "destination"; id: string; name: string; description?: string | null };

export type WhenDurationPreset = "overnight" | "3-days" | "1-week" | "2-weeks" | "1-month" | "3-months";

export const WHEN_DURATION_PRESETS: WhenDurationPreset[] = [
  "overnight",
  "3-days",
  "1-week",
  "2-weeks",
  "1-month",
  "3-months",
];

// i18n-tasks-use t('components.search_bar.duration_overnight')
// i18n-tasks-use t('components.search_bar.duration_3_days')
// i18n-tasks-use t('components.search_bar.duration_1_week')
// i18n-tasks-use t('components.search_bar.duration_2_weeks')
// i18n-tasks-use t('components.search_bar.duration_1_month')
// i18n-tasks-use t('components.search_bar.duration_3_months')
export const WHEN_DURATION_I18N_KEYS: Record<WhenDurationPreset, string> = {
  overnight: "components.search_bar.duration_overnight",
  "3-days": "components.search_bar.duration_3_days",
  "1-week": "components.search_bar.duration_1_week",
  "2-weeks": "components.search_bar.duration_2_weeks",
  "1-month": "components.search_bar.duration_1_month",
  "3-months": "components.search_bar.duration_3_months",
};

/**
 * Color family a When option renders in. Drives both the option's icon and its
 * hover/select pill tint (see WhenPopover). Months cycle through the four in
 * calendar order; durations map by trip length.
 */
export type WhenColor = "blue" | "green" | "yellow" | "orange";

const MONTH_COLOR_CYCLE: WhenColor[] = ["blue", "green", "yellow", "orange"];

/** Calendar month (1–12) → color family, looping blue/green/yellow/orange. */
export const whenMonthColor = (month: number): WhenColor =>
  MONTH_COLOR_CYCLE[(month - 1) % MONTH_COLOR_CYCLE.length] ?? "blue";

const MONTH_ICONS: StickerIconName[] = [
  "dateJan",
  "dateFeb",
  "dateMar",
  "dateApr",
  "dateMay",
  "dateJun",
  "dateJul",
  "dateAug",
  "dateSep",
  "dateOct",
  "dateNov",
  "dateDec",
];

/** Calendar month (1–12) → its colored calendar icon. */
export const whenMonthIcon = (month: number): StickerIconName => MONTH_ICONS[month - 1] ?? "date";

/** Duration preset → color family (orange shortest → blue longest). */
export const WHEN_DURATION_COLORS: Record<WhenDurationPreset, WhenColor> = {
  overnight: "orange",
  "3-days": "yellow",
  "1-week": "green",
  "2-weeks": "green",
  "1-month": "blue",
  "3-months": "blue",
};

/**
 * Color family → its stopwatch icon. The wedge sweep encodes trip length
 * (duration1 shortest → duration4 longest), matching WHEN_DURATION_COLORS.
 */
const DURATION_ICONS: Record<WhenColor, StickerIconName> = {
  blue: "duration4",
  green: "duration3",
  yellow: "duration2",
  orange: "duration1",
};

export const whenDurationIcon = (color: WhenColor): StickerIconName => DURATION_ICONS[color];

export type WhenDatePart =
  | { kind: "month"; year: number; month: number }
  | { kind: "date"; value: DateValue }
  | { kind: "duration"; preset: WhenDurationPreset };

export interface WhenSelection {
  start: WhenDatePart | null;
  end: WhenDatePart | null;
}

/**
 * A household member selectable in the Who section. Structurally matches the backend
 * `SearchMember` serializer (`family_members` shared prop): the colour + initial are
 * derived server-side and the age is computed from the member's date of birth
 * (`null` when unknown). `id` is the person's — it is what the who/member_ids params
 * carry — while `title` and `guest` come from the membership, so the same person can
 * be a guest here and family in their own household.
 */
export interface SearchMember {
  id: number;
  name: string | null;
  initial: string;
  color: AvatarColor;
  avatar?: ImageType | null;
  age: number | null;
  /** Role label shown under the name alongside the age, e.g. "Alfie's friend". */
  title: string | null;
  guest: boolean;
}

/** The Who selection: which family members are coming. */
export interface WhoSelection {
  memberIds: number[];
}
